import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Page from "../../../Page";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import { updateResearchCardPostdoc } from "../../../../core/service/researchCardPostdoc.service";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Swal from "sweetalert2";
import Label from "../../../Label";
import moment from "moment";
import excelJs from "exceljs";
import {
  Card,
  Container,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "name", label: "Name", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "mobileNumber", label: "Mobile Number", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_POSTDOC_3Y = "post_doc_3y";
const ROLE_POSTDOC_5Y = "post_doc_5y";
const ROLE_PHD = "PhD";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

ResearcherCardAdminTable.propTypes = {
  listResearchCard: PropTypes.array,
};

export default function ResearcherCardAdminTable({ listResearchCard }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openApprove, setOpenApprove] = useState(false);
  const [nameInModal, setNameInModal] = useState("");

  const closeName = () => {
    setOpenApprove(false);
  };

  const approveResearcherCard = (mip_id) => {
    Swal.fire({
      title: "Are you resubmission?",
      text: `Do you confirm to approve ${nameInModal} resubmission?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then((result) => {
      if (result.isConfirmed) {
        updateResearchCardPostdoc(mip_id, 1).then((req, res) => {
          Swal.fire({
            icon: "success",
            title: "Successfully",
            showConfirmButton: false,
            timer: 1500,
          });
        });
      }
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - listResearchCard.length)
      : 0;

  const filteredUsers = applySortFilter(
    listResearchCard,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Email",
        key: "email",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Mobile Number",
        key: "mobileNumber",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.email,
          val.mobile_number_th,
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `RESEARCHER_CARD_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Researcher Card Admin">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Researcher Card
          </Typography>
          <Link href={`${config.host_api}export/research-card-postdoc`}>
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={listResearchCard.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    const {
                      admin_approve,
                      id,
                      name_en,
                      middle_en,
                      email,
                      mobile_number_th,
                      surname_en,
                    } = row;

                    return (
                      <TableRow tabIndex={-1} hover key={i}>
                        <TableCell />

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{email}</TableCell>

                        <TableCell align="left">{mobile_number_th}</TableCell>

                        <TableCell align="left">
                          {admin_approve !== 0 ? (
                            <Label variant="ghost" color={"success"}>
                              Approve
                            </Label>
                          ) : (
                            <IconButton
                              color="success"
                              onClick={() => {
                                approveResearcherCard(id);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          )}
                        </TableCell>

                        <Dialog
                          open={openApprove}
                          onClose={closeName}
                          fullWidth={true}
                        >
                          <DialogContent>
                            Are you sure that you complete{nameInModal}{" "}
                            researcher card?
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={closeName} autoFocus>
                              Confirm
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={listResearchCard.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
